import { AIRCRAFT_TYPES, aircraftType } from '../data/aircraft';
import { AIRPORTS, airportById } from '../data/airports';
import { bankLoanAnnualRate, bankLoanAverageWeeklyInterest, bankLoanCapacity, bankLoanMonthlyInterest, BANK_LOAN_INTEREST_INTERVAL_WEEKS, BANK_LOAN_TERM_WEEKS } from '../simulation/credit';
import { estimateRouteStats, forecastRoute, hubBuildCost, hubFeeDiscountFactor, hubWeeklyCost, routeLaunchCost } from '../simulation/economy';
import { LEASE_MINIMUM_WEEKS, leaseDeposit, leaseWeeklyPayment } from '../simulation/leasing';
import { annuityPayment, clamp, distanceKm } from '../simulation/math';
import { startAuction } from './auction';
import { canAiListCompany, delistCompany, issueAiCompanyShares, listAiCompany, maxAiShareIssue, updateAiCompanySharePrice } from './listing';
import { startAiVentureRound } from './capitalMarkets';
import { emptyRouteStats } from './createGame';
import type { AircraftAsset, AiDecision, AiManager, AiRouteMemory, AiRouteTactic, GameEvent, GameState, HubAsset, Route, RouteStats } from './types';
import { AI_BALANCE } from './aiBalance';

const AI_NAMES = ['环海航空', '星桥航业', '蓝际快线', '远洲航空', '云港联航', '晨翼航空', '极点航运', '天衡航空'];
const ADVANCED_AI_NAMES = ['寰宇收益航空', '量衡航空', '远策航运', '高塔航空'];
const STRATEGIC_AI_NAMES = ['博弈枢纽航空', '锋价航空', '棋盘联航', '远谋航空'];
const ELITE_AI_NAMES = ['天穹资本航空', '阿尔法航权集团', '无界航空', '顶点控股航空'];
const AI_HEADQUARTERS = ['HKG', 'SIN', 'TPE', 'ICN', 'NRT', 'BKK', 'KUL', 'PVG', 'PEK', 'DXB', 'LHR', 'JFK'];
const AI_STARTING_CASH = 48_000_000;
const ADVANCED_AI_STARTING_CASH = 78_000_000;
const STRATEGIC_AI_STARTING_CASH = 92_000_000;
const ELITE_AI_STARTING_CASH = 150_000_000;
const AI_CASH_RESERVE = 4_000_000;
const MAX_AI_DECISIONS = 24;
const MAX_AI_DECISIONS_PER_WEEK = 3;
const STRATEGIC_COMPETITION_ADVANTAGE = 1.25;
const AI_ROUTE_MEMORY_ALPHA = 0.35;

interface AiAggression {
  cashReserve: number;
  downPaymentRatio: number;
}

function aiAggression(manager: AiManager): AiAggression {
  const profile = AI_BALANCE[manager.strategy];
  switch (manager.strategy) {
    case 'elite':
      return { cashReserve: 5_000_000 * profile.cashReserveMultiplier, downPaymentRatio: profile.downPaymentRatio };
    case 'strategic':
      return { cashReserve: 2_000_000 * profile.cashReserveMultiplier, downPaymentRatio: profile.downPaymentRatio };
    case 'advanced':
      return { cashReserve: 3_000_000 * profile.cashReserveMultiplier, downPaymentRatio: profile.downPaymentRatio };
    case 'basic':
    default:
      return { cashReserve: 4_000_000 * profile.cashReserveMultiplier, downPaymentRatio: profile.downPaymentRatio };
  }
}

const clone = <T,>(value: T): T => structuredClone(value);
const routeKey = (a: string, b: string) => [a, b].sort().join('-');

function nextId(state: GameState, prefix: string): string {
  const value = `${prefix}-${state.nextId}`;
  state.nextId += 1;
  return value;
}

function recordDecision(manager: AiManager, decision: Omit<AiDecision, 'week' | 'cashAfter'>, week: number): void {
  manager.lastDecisionWeek = week;
  manager.decisions = [
    ...(manager.decisions ?? []),
    {
      ...decision,
      week,
      cashAfter: Math.round(manager.cash),
    },
  ].slice(-MAX_AI_DECISIONS);
}

function generatedName(names: string[], index: number): string {
  const cycle = Math.floor(index / names.length);
  const baseName = names[index % names.length];
  return cycle === 0 ? baseName : `${baseName} ${cycle + 1}`;
}

function asAiGameState(parent: GameState, manager: AiManager): GameState {
  return {
    ...parent,
    cash: manager.cash,
    reputation: manager.reputation,
    headquartersId: manager.headquartersId,
    hubs: manager.hubs,
    aircraft: manager.aircraft,
    routes: manager.routes,
    loans: manager.loans ?? [],
    reports: [],
    ledger: [],
    activeEffects: parent.activeEffects,
    activeMarketEvents: parent.activeMarketEvents,
    assistants: { unlockedIds: [], dismissedIds: [], hired: [] },
    fuelInventory: { gallons: 0, averageCost: 0 },
    fuelFutures: [],
    aiManagers: (parent.aiManagers ?? []).filter((item) => item.id !== manager.id),
  };
}

function sellAiAircraft(parent: GameState, manager: AiManager, aircraft: AircraftAsset): number {
  const salePrice = Math.round(aircraft.bookValue * (0.72 + aircraft.condition * 0.18));
  const loan = aircraft.loanId ? (manager.loans ?? []).find((item) => item.id === aircraft.loanId) : undefined;
  const payoff = loan?.principalRemaining ?? 0;
  const proceeds = salePrice - payoff;
  manager.cash += proceeds;

  for (const routeId of aircraft.routeIds) {
    const route = manager.routes.find((item) => item.id === routeId);
    if (route) {
      route.aircraftIds = route.aircraftIds.filter((aircraftId) => aircraftId !== aircraft.id);
      if (route.aircraftIds.length === 0) {
        route.active = false;
        route.stats = emptyRouteStats(route.stats?.distanceKm ?? 0);
      }
    }
  }
  manager.aircraft = manager.aircraft.filter((item) => item.id !== aircraft.id);
  if (loan) manager.loans = manager.loans.filter((item) => item.id !== loan.id);
  void parent;
  return proceeds;
}

function closeAiRoute(manager: AiManager, route: Route): void {
  route.active = false;
  for (const aircraftId of route.aircraftIds) {
    const aircraft = manager.aircraft.find((item) => item.id === aircraftId);
    if (aircraft) aircraft.routeIds = aircraft.routeIds.filter((routeId) => routeId !== route.id);
  }
  route.aircraftIds = [];
}

function reconcileAiAssignments(manager: AiManager): void {
  const aircraftById = new Map(manager.aircraft.map((aircraft) => [aircraft.id, aircraft]));
  const activeRouteIds = new Set(manager.routes.filter((route) => route.active).map((route) => route.id));

  for (const aircraft of manager.aircraft) {
    aircraft.routeIds = [...new Set(aircraft.routeIds ?? [])].filter((routeId) => activeRouteIds.has(routeId));
  }

  for (const route of manager.routes) {
    const validAircraftIds = [...new Set(route.aircraftIds ?? [])].filter((aircraftId) => aircraftById.has(aircraftId));
    route.aircraftIds = validAircraftIds;
    for (const aircraftId of validAircraftIds) {
      const aircraft = aircraftById.get(aircraftId);
      if (aircraft && !aircraft.routeIds.includes(route.id)) aircraft.routeIds.push(route.id);
    }
    if (route.active && validAircraftIds.length === 0 && (route.stats?.seats ?? 0) <= 0) {
      route.active = false;
      route.stats = emptyRouteStats(route.stats?.distanceKm ?? 0);
    }
  }
}

function handleAiInsolvency(parent: GameState, manager: AiManager): 'solvent' | 'restructured' | 'bankrupt' {
  if (manager.cash >= 0) return 'solvent';

  const sold: string[] = [];
  const candidates = [...manager.aircraft].sort((a, b) => a.bookValue - b.bookValue);
  while (manager.cash < 0 && candidates.length > 0) {
    const aircraft = candidates.shift()!;
    sellAiAircraft(parent, manager, aircraft);
    sold.push(aircraftType(aircraft.typeId).name);
  }

  if (manager.cash < 0) {
    const aiState = asAiGameState(parent, manager);
    const capacity = bankLoanCapacity(aiState);
    const borrow = Math.min(capacity, -manager.cash);
    if (borrow > 0) {
      const annualRate = bankLoanAnnualRate(aiState);
      manager.cash += borrow;
      manager.loans.push({
        id: nextId(parent, 'ai-loan'),
        kind: 'bank',
        originalPrincipal: borrow,
        principalRemaining: borrow,
        annualRate,
        weeklyPayment: bankLoanAverageWeeklyInterest(borrow, annualRate),
        weeksRemaining: BANK_LOAN_TERM_WEEKS,
      });
    }
  }

  if (manager.cash < 0) {
    const deficit = -manager.cash;
    manager.cash = 0;
    manager.bankrupt = {
      week: parent.week,
      acquiredFor: Math.max(0, deficit),
      message: `${manager.name} 现金枯竭，资产出售与授信救助后仍无法覆盖缺口。`,
    };
    manager.routes = manager.routes.map((route) => ({ ...route, active: false }));
    for (const aircraft of manager.aircraft) aircraft.routeIds = [];
    recordDecision(manager, {
      kind: 'hold',
      summary: '现金枯竭并进入破产清算',
    }, parent.week);
    return 'bankrupt';
  }

  if (sold.length) {
    recordDecision(manager, {
      kind: 'hold',
      summary: `强制出售 ${sold.join('、')} 完成现金自救`,
    }, parent.week);
    return 'restructured';
  }

  return 'restructured';
}

function createAiAircraft(state: GameState, manager: AiManager, typeId: string): AircraftAsset {
  const type = aircraftType(typeId);
  return {
    id: nextId(state, 'ai-aircraft'),
    typeId,
    acquisition: 'new',
    condition: 1,
    purchasePrice: type.price,
    bookValue: type.price,
    acquiredWeek: state.week,
    weeksOwned: 0,
    totalCycles: 0,
    totalFlightHours: 0,
    speedModifier: 1,
    fareRatio: 100,
    crewSalaryModifier: 1,
    routeIds: [],
    crewMood: 60,
    crewHiredWeek: state.week,
  };
}

function createAiLeasedAircraft(state: GameState, manager: AiManager, typeId: string): AircraftAsset {
  const type = aircraftType(typeId);
  void manager;
  return {
    id: nextId(state, 'ai-aircraft'),
    typeId,
    acquisition: 'leased',
    condition: 1,
    purchasePrice: 0,
    bookValue: 0,
    acquiredWeek: state.week,
    weeksOwned: 0,
    totalCycles: 0,
    totalFlightHours: 0,
    speedModifier: 1,
    fareRatio: 100,
    crewSalaryModifier: 1,
    routeIds: [],
    crewMood: 60,
    crewHiredWeek: state.week,
    lease: {
      weeklyPayment: leaseWeeklyPayment(type),
      deposit: leaseDeposit(type),
      deliveryWeek: state.week,
      minimumWeeks: LEASE_MINIMUM_WEEKS,
    },
  };
}

function routeStatsFromForecast(forecast: ReturnType<typeof forecastRoute>): RouteStats {
  return {
    distanceKm: forecast.distanceKm,
    roundTrips: 0,
    seats: forecast.seats,
    demand: forecast.demand,
    passengers: forecast.passengers,
    localPassengers: forecast.passengers,
    transferPassengers: 0,
    loadFactor: forecast.loadFactor,
    unmetDemand: Math.max(0, forecast.demand - forecast.passengers),
    revenue: forecast.revenue,
    cashCost: forecast.cashCost,
    operatingProfit: forecast.weeklyProfit,
    networkContribution: 0,
    cabins: forecast.cabins,
  };
}

function chooseAircraftType(manager: AiManager): string | null {
  const aggression = aiAggression(manager);
  const affordable = AIRCRAFT_TYPES
    .filter((type) => type.minReputation <= manager.reputation)
    .filter((type) => manager.cash - Math.round(type.price * aggression.downPaymentRatio) >= aggression.cashReserve)
    .sort((a, b) => b.price - a.price);
  const preferred = affordable.find((type) => manager.cash > 95_000_000 && type.rangeKm >= 5_000)
    ?? affordable.find((type) => manager.cash > 60_000_000 && type.id === 'E190E2')
    ?? affordable.find((type) => type.id === 'ATR72');
  return preferred?.id ?? null;
}

function chooseLeaseAircraftType(manager: AiManager): string | null {
  const aggression = aiAggression(manager);
  const affordable = AIRCRAFT_TYPES
    .filter((type) => type.minReputation <= manager.reputation)
    .filter((type) => manager.cash - leaseDeposit(type) >= aggression.cashReserve)
    .sort((a, b) => b.rangeKm - a.rangeKm || b.price - a.price);
  return affordable.find((type) => manager.cash > 28_000_000 && type.id === 'E190E2')?.id
    ?? affordable.find((type) => type.id === 'ATR72')?.id
    ?? affordable[0]?.id
    ?? null;
}

function scoreRoute(manager: AiManager, forecast: ReturnType<typeof forecastRoute>): number {
  if (manager.strategy === 'advanced' || manager.strategy === 'strategic' || manager.strategy === 'elite') {
    return forecast.weeklyProfit;
  }
  return forecast.weeklyProfit * (0.65 + forecast.loadFactor) + Math.max(0, forecast.demand - forecast.passengers) * 18;
}

function routeTactic(manager: AiManager, forecast: ReturnType<typeof forecastRoute>, isPlayerRoute: boolean): AiRouteTactic | undefined {
  if (manager.strategy !== 'strategic' && manager.strategy !== 'elite') return undefined;
  if (isPlayerRoute) return 'price_war';
  if (forecast.loadFactor > 0.9) return 'yield_management';
  if (forecast.demand > forecast.seats * 1.2) return 'capacity_pressure';
  return 'market_probe';
}

function aiFareRatio(tactic: AiRouteTactic | undefined): number {
  if (tactic === 'price_war') return 65;
  if (tactic === 'yield_management') return 140;
  return 100;
}

function priceWarResponseFare(manager: AiManager, competitorFareRatio: number): number {
  const floor = manager.strategy === 'basic' ? 40 : manager.strategy === 'advanced' ? 35 : 30;
  return Math.max(floor, Math.min(95, Math.ceil(competitorFareRatio + 5)));
}

function tryRespondToPriceWar(parent: GameState, manager: AiManager): boolean {
  const aggression = aiAggression(manager);
  if (manager.cash < aggression.cashReserve * 2) return false;

  for (const route of manager.routes.filter((item) => item.active)) {
    const key = routeKey(route.originId, route.destinationId);
    const competitors = [
      ...parent.routes.filter((item) => item.active && routeKey(item.originId, item.destinationId) === key),
      ...parent.aiManagers
        .filter((item) => item.id !== manager.id && !item.bankrupt)
        .flatMap((item) => item.routes.filter((candidate) => candidate.active && routeKey(candidate.originId, candidate.destinationId) === key)),
    ];
    const lowestFare = Math.min(...competitors.map((item) => item.fareRatio ?? 100));
    if (route.aiTactic === 'price_war' && (route.fareRatio ?? 100) >= 100) {
      route.fareRatio = 65;
      for (const aircraftId of route.aircraftIds) {
        const aircraft = manager.aircraft.find((item) => item.id === aircraftId);
        if (aircraft) aircraft.fareRatio = 65;
      }
    }
    if (!Number.isFinite(lowestFare) || lowestFare >= 80 || lowestFare >= (route.fareRatio ?? 100) - 8) continue;

    const nextFare = priceWarResponseFare(manager, lowestFare);
    if (nextFare >= (route.fareRatio ?? 100)) continue;
    route.fareRatio = nextFare;
    for (const aircraftId of route.aircraftIds) {
      const aircraft = manager.aircraft.find((item) => item.id === aircraftId);
      if (aircraft) aircraft.fareRatio = nextFare;
    }
    route.aiTactic = 'price_war';
    recordDecision(manager, {
      kind: 'hold',
      summary: `回应价格战，下调 ${route.originId}-${route.destinationId} 票价至 ${nextFare}%`,
    }, parent.week);
    return true;
  }

  return false;
}

function chooseRoute(parent: GameState, manager: AiManager, aircraft: AircraftAsset) {
  const existingPairs = new Set(manager.routes.filter((route) => route.active).map((route) => routeKey(route.originId, route.destinationId)));
  const candidates = [];

  for (const hub of manager.hubs) {
    const origin = airportById(hub.airportId);
    for (const destination of AIRPORTS) {
      if (destination.iata === origin.iata) continue;
      if (existingPairs.has(routeKey(origin.iata, destination.iata))) continue;
      if (distanceKm(origin, destination) > aircraftType(aircraft.typeId).rangeKm) continue;
      if (aircraftType(aircraft.typeId).runwayClass > origin.runwayClass || aircraftType(aircraft.typeId).runwayClass > destination.runwayClass) continue;
      const launchCost = routeLaunchCost(origin, destination);
      if (manager.cash - launchCost < aiAggression(manager).cashReserve) continue;
      const pair = routeKey(origin.iata, destination.iata);
      const forecastState = { ...parent, routes: parent.routes.filter((route) => routeKey(route.originId, route.destinationId) !== pair) };
      const forecast = forecastRoute(forecastState, origin.iata, destination.iata, aircraft, undefined, manager);
      if (forecast.weeklyProfit <= 0) continue;
      const isPlayerRoute = parent.routes.some((route) => route.active && routeKey(route.originId, route.destinationId) === routeKey(origin.iata, destination.iata));
      candidates.push({
        originId: origin.iata,
        destinationId: destination.iata,
        launchCost,
        forecast,
        isPlayerRoute,
        aiTactic: routeTactic(manager, forecast, isPlayerRoute),
        score: scoreRoute(manager, forecast),
      });
    }
  }

  candidates.sort((a, b) => b.score - a.score || a.destinationId.localeCompare(b.destinationId));
  if (manager.strategy === 'strategic') {
    const playerCandidates = candidates.filter((candidate) => candidate.isPlayerRoute);
    const nonPlayerCandidates = candidates.filter((candidate) => !candidate.isPlayerRoute);
    const bestPlayer = playerCandidates[0];
    const bestAlternative = nonPlayerCandidates[0];
    if (!bestPlayer) return bestAlternative;
    if (!bestAlternative || bestPlayer.forecast.weeklyProfit > bestAlternative.forecast.weeklyProfit * STRATEGIC_COMPETITION_ADVANTAGE) {
      return bestPlayer;
    }
    return bestAlternative;
  }
  return candidates[0];
}

function updateRouteMemory(manager: AiManager, route: Route, week: number): void {
  manager.routeMemory ??= {};
  const previous = manager.routeMemory[route.id];
  const observed = {
    revenue: route.stats.revenue,
    operatingProfit: route.stats.operatingProfit,
    loadFactor: route.stats.loadFactor,
  };
  if (!previous) {
    manager.routeMemory[route.id] = {
      routeId: route.id,
      ...observed,
      weeksObserved: 1,
      trailingRevenue: observed.revenue,
      trailingOperatingProfit: observed.operatingProfit,
      trailingLoadFactor: observed.loadFactor,
      lastUpdatedWeek: week,
    };
    return;
  }

  manager.routeMemory[route.id] = {
    ...previous,
    ...observed,
    weeksObserved: previous.weeksObserved + 1,
    trailingRevenue: previous.trailingRevenue * (1 - AI_ROUTE_MEMORY_ALPHA) + observed.revenue * AI_ROUTE_MEMORY_ALPHA,
    trailingOperatingProfit: previous.trailingOperatingProfit * (1 - AI_ROUTE_MEMORY_ALPHA) + observed.operatingProfit * AI_ROUTE_MEMORY_ALPHA,
    trailingLoadFactor: previous.trailingLoadFactor * (1 - AI_ROUTE_MEMORY_ALPHA) + observed.loadFactor * AI_ROUTE_MEMORY_ALPHA,
    lastUpdatedWeek: week,
  };
}

function routeMemory(manager: AiManager, route: Route): AiRouteMemory | undefined {
  return manager.routeMemory?.[route.id];
}

function settleAiWeek(parent: GameState, manager: AiManager): void {
  const aiState = asAiGameState(parent, manager);
  let operatingProfit = 0;
  let seats = 0;
  let passengers = 0;

  for (const route of manager.routes.filter((item) => item.active)) {
    route.reputationScore = route.reputationScore || 25;
    route.fareRatio ??= 100;
    // 保留没有飞机但仍处于市场中的路线信号，避免 AI 短暂调度空窗时从竞争格局中消失。
    if (route.aircraftIds.length > 0) route.stats = estimateRouteStats(parent, route, manager);
    route.maturityWeeks += 1;
    operatingProfit += route.stats.operatingProfit;
    seats += route.stats.seats;
    passengers += route.stats.passengers;
    updateRouteMemory(manager, route, parent.week);
  }

  const hubCosts = Math.round(manager.hubs.reduce((sum, hub) => sum + hub.weeklyCost, 0) * hubFeeDiscountFactor(parent.week));
  let debtService = 0;
  for (const loan of manager.loans ?? []) {
    if (loan.kind === 'bank' && loan.weeksRemaining > 0) {
      loan.weeksRemaining -= 1;
      if (loan.weeksRemaining % BANK_LOAN_INTEREST_INTERVAL_WEEKS === 0) {
        debtService += bankLoanMonthlyInterest(loan.principalRemaining, loan.annualRate);
      }
      continue;
    }
    if (loan.kind === 'aircraft') {
      const weeklyInterest = loan.principalRemaining * loan.annualRate / 52;
      const payment = Math.min(loan.weeklyPayment, loan.principalRemaining + weeklyInterest);
      const principal = Math.min(loan.principalRemaining, Math.max(0, payment - weeklyInterest));
      debtService += payment;
      loan.principalRemaining -= principal;
      loan.weeksRemaining -= 1;
    }
  }
  manager.loans = (manager.loans ?? []).filter((loan) => loan.kind === 'bank' || (loan.principalRemaining > 1 && loan.weeksRemaining > 0));
  manager.cash += operatingProfit - hubCosts - debtService;
  manager.reputation = clamp(manager.reputation + (seats > 0 && passengers / seats > 0.7 ? 0.06 : -0.01), 30, 92);

  for (const aircraft of manager.aircraft) {
    aircraft.weeksOwned += 1;
    aircraft.bookValue = Math.max(aircraft.purchasePrice * 0.12, aircraft.bookValue - aircraft.purchasePrice / (20 * 52));
    if (aircraft.routeIds.length) aircraft.condition = clamp(Math.round((aircraft.condition - 0.002) * 100) / 100, 0.58, 1);
  }
}

function tryCloseUnprofitableRoute(parent: GameState, manager: AiManager): boolean {
  const threshold = manager.strategy === 'basic' ? -35_000 : manager.strategy === 'advanced' ? -55_000 : -85_000;
  const candidates = manager.routes
    .filter((route) => route.active && route.maturityWeeks >= 10)
    .map((route) => ({ route, memory: routeMemory(manager, route) }))
    .filter((item): item is { route: Route; memory: AiRouteMemory } => Boolean(item.memory && item.memory.weeksObserved >= 6))
    .filter(({ route, memory }) => {
      const strategicFeeder = route.aiTactic === 'capacity_pressure' && manager.routes.filter((item) => item.active && [item.originId, item.destinationId].includes(route.originId)).length >= 3;
      return !strategicFeeder && memory.trailingOperatingProfit < threshold && memory.trailingLoadFactor < 0.68;
    })
    .sort((a, b) => a.memory.trailingOperatingProfit - b.memory.trailingOperatingProfit);
  const target = candidates[0];
  if (!target) return false;

  closeAiRoute(manager, target.route);
  recordDecision(manager, {
    kind: 'close_route',
    summary: `关停 ${target.route.originId}-${target.route.destinationId}：近 ${target.memory.weeksObserved} 周均利 ${Math.round(target.memory.trailingOperatingProfit).toLocaleString('zh-CN')}`,
  }, parent.week);
  return true;
}

function tryListCompany(parent: GameState, manager: AiManager): boolean {
  if (!canAiListCompany(manager, parent.week)) return false;
  listAiCompany(manager, parent.week);
  recordDecision(manager, {
    kind: 'list_company',
    summary: '完成上市并获得公开市场融资',
  }, parent.week);
  return true;
}

function tryIssueAiShares(parent: GameState, manager: AiManager): boolean {
  const listing = manager.companyListing;
  const maxIssue = maxAiShareIssue(manager);
  if (!listing || maxIssue <= 0) return false;

  const targetCash = aiAggression(manager).cashReserve * 2;
  if (manager.cash >= targetCash) return false;
  const price = listing.sharePrice || listing.lastValuation / listing.totalShares;
  if (!Number.isFinite(price) || price <= 0) return false;

  const cashGap = Math.max(1, targetCash - manager.cash);
  const requestedQuantity = Math.max(1, Math.ceil(cashGap / price));
  const issued = issueAiCompanyShares(manager, parent.week, Math.min(maxIssue, requestedQuantity));
  if (!issued) return false;
  recordDecision(manager, {
    kind: 'issue_shares',
    summary: `增发 ${issued.toLocaleString('zh-CN')} 股，补充扩张现金`,
  }, parent.week);
  return true;
}

function tryStartAiRoadshow(parent: GameState, manager: AiManager): boolean {
  const listing = manager.companyListing;
  const reserve = aiAggression(manager).cashReserve;
  if (!listing?.listed || manager.cash < reserve * 0.5 || manager.cash >= reserve * 2) return false;
  if ((parent.ventureRounds ?? []).some((round) => round.companyId === manager.id && round.status === 'open')) return false;
  const price = listing.sharePrice || listing.lastValuation / listing.totalShares;
  const maxIssue = maxAiShareIssue(manager);
  if (!Number.isFinite(price) || price <= 0 || maxIssue <= 0) return false;
  const target = Math.min(maxIssue * price, Math.max(price, aiAggression(manager).cashReserve * 8));
  if (target <= 0) return false;
  const next = startAiVentureRound(parent, manager.id, target);
  parent.ventureRounds = next.ventureRounds;
  parent.nextId = next.nextId;
  recordDecision(manager, {
    kind: 'roadshow',
    summary: `发起 ${Math.round(target).toLocaleString('zh-CN')} 元 AI 路演，向投资者融资`,
  }, parent.week);
  return true;
}

function tryRetireWeakAircraft(parent: GameState, manager: AiManager): boolean {
  const target = manager.aircraft
    .filter((aircraft) => aircraft.routeIds.length === 0)
    .filter((aircraft) => aircraft.condition < 0.62 || aircraft.weeksOwned > 520)
    .sort((a, b) => a.condition - b.condition || b.weeksOwned - a.weeksOwned)[0];
  if (!target) return false;

  const type = aircraftType(target.typeId);
  if (target.lease) {
    manager.cash += Math.round(target.lease.deposit * 0.65);
    manager.aircraft = manager.aircraft.filter((aircraft) => aircraft.id !== target.id);
    recordDecision(manager, {
      kind: 'retire_aircraft',
      summary: `退租低效 ${type.name}`,
    }, parent.week);
    return true;
  }

  const proceeds = sellAiAircraft(parent, manager, target);
  recordDecision(manager, {
    kind: 'retire_aircraft',
    summary: `出售低效 ${type.name}，回收 ${Math.round(proceeds).toLocaleString('zh-CN')}`,
  }, parent.week);
  return true;
}

function maintainAircraft(manager: AiManager, aircraft: AircraftAsset): boolean {
  const type = aircraftType(aircraft.typeId);
  const cost = Math.round(type.price * 0.0045);
  if (manager.cash - cost < aiAggression(manager).cashReserve) return false;
  manager.cash -= cost;
  aircraft.condition = clamp(aircraft.condition + 0.14, 0.58, 1);
  return true;
}

function tryMaintainAircraft(parent: GameState, manager: AiManager): boolean {
  const maintenanceTarget = manager.aircraft.find((aircraft) => aircraft.condition < 0.72);
  if (maintenanceTarget && maintainAircraft(manager, maintenanceTarget)) {
    recordDecision(manager, {
      kind: 'maintain_aircraft',
      summary: `维护 ${aircraftType(maintenanceTarget.typeId).name}`,
    }, parent.week);
    return true;
  }
  return false;
}

function tryOpenRoute(parent: GameState, manager: AiManager): boolean {
  const idleAircraft = manager.aircraft.find((aircraft) => aircraft.routeIds.length === 0);
  if (!idleAircraft) return false;
  const routeChoice = chooseRoute(parent, manager, idleAircraft);
  if (!routeChoice) return false;
  const routeId = nextId(parent, 'ai-route');
  const fareRatio = aiFareRatio(routeChoice.aiTactic);
  idleAircraft.routeIds.push(routeId);
  idleAircraft.fareRatio = fareRatio;
  manager.cash -= routeChoice.launchCost;
  manager.routes.push({
    id: routeId,
    originId: routeChoice.originId,
    destinationId: routeChoice.destinationId,
    aircraftIds: [idleAircraft.id],
    openedWeek: parent.week,
    maturityWeeks: 0,
    launchInvestment: routeChoice.launchCost,
    active: true,
    reputationScore: 25,
    aiTactic: routeChoice.aiTactic,
    fareRatio,
    stats: routeStatsFromForecast(routeChoice.forecast),
  });
  recordDecision(manager, {
    kind: 'open_route',
    summary: routeChoice.aiTactic === 'price_war'
      ? `开通 ${routeChoice.originId}-${routeChoice.destinationId} 并发起价格战`
      : `开通 ${routeChoice.originId}-${routeChoice.destinationId}`,
  }, parent.week);
  return true;
}

function tryBuyAircraft(parent: GameState, manager: AiManager): boolean {
  const aircraftTypeId = chooseAircraftType(manager);
  if (!aircraftTypeId) return false;
  const type = aircraftType(aircraftTypeId);
  const aggression = aiAggression(manager);
  const aircraft = createAiAircraft(parent, manager, aircraftTypeId);
  const downPayment = Math.round(type.price * aggression.downPaymentRatio);
  const financingFee = aggression.downPaymentRatio < 1 ? Math.round(type.price * 0.01) : 0;

  if (aggression.downPaymentRatio < 1) {
    const principal = type.price - downPayment;
    const loanId = nextId(parent, 'ai-loan');
    manager.loans.push({
      id: loanId,
      kind: 'aircraft',
      aircraftId: aircraft.id,
      originalPrincipal: principal,
      principalRemaining: principal,
      annualRate: 0.085,
      weeklyPayment: annuityPayment(principal, 0.085, 416),
      weeksRemaining: 416,
    });
    aircraft.loanId = loanId;
  }

  manager.cash -= downPayment + financingFee;
  manager.aircraft.push(aircraft);
  recordDecision(manager, {
    kind: 'buy_aircraft',
    summary: `采购 ${type.name}${aggression.downPaymentRatio < 1 ? '（融资）' : ''}`,
  }, parent.week);
  return true;
}

function tryLeaseAircraft(parent: GameState, manager: AiManager): boolean {
  if (manager.strategy === 'strategic' || manager.strategy === 'elite') return false;
  if (manager.aircraft.some((aircraft) => aircraft.routeIds.length === 0)) return false;
  const activeRoutes = manager.routes.filter((route) => route.active).length;
  if (activeRoutes < Math.max(1, manager.aircraft.length - 1)) return false;
  const aircraftTypeId = chooseLeaseAircraftType(manager);
  if (!aircraftTypeId) return false;
  const aircraft = createAiLeasedAircraft(parent, manager, aircraftTypeId);
  const deposit = aircraft.lease?.deposit ?? 0;
  manager.cash -= deposit;
  manager.aircraft.push(aircraft);
  recordDecision(manager, {
    kind: 'lease_aircraft',
    summary: `租赁 ${aircraftType(aircraftTypeId).name} 扩张运力`,
  }, parent.week);
  return true;
}

function hubRouteCount(manager: AiManager, hub: HubAsset): number {
  return manager.routes.filter((route) => route.active && [route.originId, route.destinationId].includes(hub.airportId)).length;
}

function chooseExpansionHub(parent: GameState, manager: AiManager): string | null {
  const existing = new Set(manager.hubs.map((hub) => hub.airportId));
  const connected = new Map<string, number>();
  for (const route of manager.routes.filter((item) => item.active)) {
    connected.set(route.originId, (connected.get(route.originId) ?? 0) + Math.max(1, route.stats.operatingProfit));
    connected.set(route.destinationId, (connected.get(route.destinationId) ?? 0) + Math.max(1, route.stats.operatingProfit));
  }

  const playerPressure = parent.routes
    .filter((route) => route.active && route.stats.operatingProfit > 80_000)
    .flatMap((route) => [route.originId, route.destinationId])
    .filter((airportId) => !existing.has(airportId));

  const candidates = AIRPORTS
    .filter((airport) => !existing.has(airport.iata))
    .filter((airport) => connected.has(airport.iata) || (manager.strategy !== 'basic' && playerPressure.includes(airport.iata)))
    .map((airport) => ({
      airport,
      score: (connected.get(airport.iata) ?? 0) + airport.market * 120_000 + airport.international * 50_000 + (playerPressure.includes(airport.iata) ? 450_000 : 0),
    }))
    .sort((a, b) => b.score - a.score);
  return candidates[0]?.airport.iata ?? null;
}

function tryBuildExpansionHub(parent: GameState, manager: AiManager): boolean {
  const routeCount = manager.routes.filter((route) => route.active).length;
  const aircraftCount = manager.aircraft.length;
  const minAircraft = manager.strategy === 'elite' ? 5 : manager.strategy === 'strategic' ? 6 : manager.strategy === 'advanced' ? 8 : 10;
  if (aircraftCount < minAircraft || routeCount < minAircraft - 1) return false;
  if (manager.hubs.some((hub) => hubRouteCount(manager, hub) < 4)) return false;
  if (manager.hubs.length >= (manager.strategy === 'elite' ? 5 : manager.strategy === 'strategic' ? 4 : 3)) return false;

  const airportId = chooseExpansionHub(parent, manager);
  if (!airportId) return false;
  const airport = airportById(airportId);
  const cost = Math.round(hubBuildCost(airport) * (manager.strategy === 'elite' ? 0.85 : 1));
  if (manager.cash - cost < aiAggression(manager).cashReserve * 2) return false;

  manager.cash -= cost;
  manager.hubs.push({
    airportId,
    level: 1,
    weeklyCost: hubWeeklyCost(airport),
    builtWeek: parent.week,
    specialization: manager.strategy === 'strategic' ? 'connection' : manager.strategy === 'elite' ? 'premium' : 'balanced',
  });
  recordDecision(manager, {
    kind: 'build_hub',
    summary: `建设 ${airportId} 第二枢纽，扩大网络覆盖`,
  }, parent.week);
  return true;
}

function advanceOneAi(parent: GameState, manager: AiManager): void {
  if (manager.human) return; // 人类玩家镜像只参与市场竞争，不由 AI 逻辑驱动
  if (manager.bankrupt) return;
  manager.strategy ??= 'basic';
  manager.decisions ??= [];
  manager.routeMemory ??= {};
  manager.noRouteWeeks ??= 0;
  manager.aircraft ??= [];
  manager.routes ??= [];
  manager.loans ??= [];
  manager.hubs ??= [{
    airportId: manager.headquartersId,
    level: 1,
    weeklyCost: hubWeeklyCost(airportById(manager.headquartersId)),
    builtWeek: 0,
    specialization: 'balanced',
  }];
  manager.cash = Number.isFinite(manager.cash) ? manager.cash : 0;
  for (const aircraft of manager.aircraft) {
    aircraft.crewMood ??= 60;
    aircraft.crewHiredWeek ??= aircraft.acquiredWeek;
    aircraft.fareRatio ??= 100;
  }
  reconcileAiAssignments(manager);

  settleAiWeek(parent, manager);
  updateAiCompanySharePrice(manager, parent.week);
  if (!tryStartAiRoadshow(parent, manager)) tryIssueAiShares(parent, manager);
  if (manager.cash < 0) tryCloseUnprofitableRoute(parent, manager);
  const solvency = handleAiInsolvency(parent, manager);
  if (solvency === 'bankrupt' || solvency === 'restructured') {
    updateAiRouteInactivity(manager);
    return;
  }
  if (tryRespondToPriceWar(parent, manager)) {
    updateAiRouteInactivity(manager);
    return;
  }

  let decisions = 0;
  while (decisions < MAX_AI_DECISIONS_PER_WEEK) {
    if (
      tryCloseUnprofitableRoute(parent, manager)
      || tryRetireWeakAircraft(parent, manager)
      || tryMaintainAircraft(parent, manager)
      || tryBuildExpansionHub(parent, manager)
      || tryOpenRoute(parent, manager)
      || tryBuyAircraft(parent, manager)
      || tryLeaseAircraft(parent, manager)
      || tryListCompany(parent, manager)
    ) {
      decisions += 1;
      continue;
    }
    break;
  }

  if (decisions === 0) {
    for (const route of manager.routes) {
      route.stats ??= emptyRouteStats();
    }
    recordDecision(manager, {
      kind: 'hold',
      summary: '保留现金并观察市场',
    }, parent.week);
  }
  updateAiRouteInactivity(manager);
}

function updateAiRouteInactivity(manager: AiManager) {
  const hasActiveRoute = manager.routes.some((route) => route.active && route.aircraftIds.length > 0);
  manager.noRouteWeeks = hasActiveRoute ? 0 : (manager.noRouteWeeks ?? 0) + 1;
}

export function addAiManagers(state: GameState, count: number): GameState {
  const requested = Math.floor(Number(count));
  if (!Number.isFinite(requested) || requested <= 0) throw new Error('请输入要加入的 AI 数量');

  const next = clone(state);
  next.aiManagers ??= [];

  for (let i = 0; i < requested; i += 1) {
    const id = nextId(next, 'ai');
    const index = next.aiManagers.length;
    const headquartersId = AI_HEADQUARTERS[(state.seed + index) % AI_HEADQUARTERS.length];
    const headquarters = airportById(headquartersId);
    const manager: AiManager = {
      id,
      name: generatedName(AI_NAMES, index),
      strategy: 'basic',
      cash: AI_STARTING_CASH,
      reputation: 50,
      headquartersId,
      hubs: [{
        airportId: headquartersId,
        level: 1,
        weeklyCost: hubWeeklyCost(headquarters),
        builtWeek: next.week,
        specialization: 'balanced',
      }],
      aircraft: [],
      routes: [],
      loans: [],
      decisions: [],
      routeMemory: {},
    };
    manager.aircraft = [createAiAircraft(next, manager, 'ATR72')];
    next.aiManagers.push(manager);
  }

  next.updatedAt = Date.now();
  return next;
}

export function addAdvancedAiManager(state: GameState): GameState {
  const next = clone(state);
  next.aiManagers ??= [];
  const advancedIndex = next.aiManagers.filter((manager) => manager.strategy === 'advanced').length;
  const globalIndex = next.aiManagers.length;
  const id = nextId(next, 'ai');
  const headquartersId = AI_HEADQUARTERS[(state.seed + globalIndex + 3) % AI_HEADQUARTERS.length];
  const headquarters = airportById(headquartersId);
  const manager: AiManager = {
    id,
    name: generatedName(ADVANCED_AI_NAMES, advancedIndex),
    strategy: 'advanced',
    cash: ADVANCED_AI_STARTING_CASH,
    reputation: 58,
    headquartersId,
    hubs: [{
      airportId: headquartersId,
      level: 1,
      weeklyCost: hubWeeklyCost(headquarters),
      builtWeek: next.week,
      specialization: 'balanced',
    }],
    aircraft: [],
    routes: [],
    loans: [],
    decisions: [],
    routeMemory: {},
  };
  manager.aircraft = [createAiAircraft(next, manager, 'ARJ21')];
  next.aiManagers.push(manager);
  next.updatedAt = Date.now();
  return next;
}

export function addStrategicAiManager(state: GameState): GameState {
  const next = clone(state);
  next.aiManagers ??= [];
  const strategicIndex = next.aiManagers.filter((manager) => manager.strategy === 'strategic').length;
  const globalIndex = next.aiManagers.length;
  const id = nextId(next, 'ai');
  const headquartersId = AI_HEADQUARTERS[(state.seed + globalIndex + 5) % AI_HEADQUARTERS.length];
  const headquarters = airportById(headquartersId);
  const manager: AiManager = {
    id,
    name: generatedName(STRATEGIC_AI_NAMES, strategicIndex),
    strategy: 'strategic',
    cash: STRATEGIC_AI_STARTING_CASH,
    reputation: 62,
    headquartersId,
    hubs: [{
      airportId: headquartersId,
      level: 1,
      weeklyCost: hubWeeklyCost(headquarters),
      builtWeek: next.week,
      specialization: 'balanced',
    }],
    aircraft: [],
    routes: [],
    loans: [],
    decisions: [],
    routeMemory: {},
  };
  manager.aircraft = [createAiAircraft(next, manager, 'E190E2')];
  next.aiManagers.push(manager);
  next.updatedAt = Date.now();
  return next;
}

export function addEliteAiManager(state: GameState): GameState {
  const next = clone(state);
  next.aiManagers ??= [];
  const eliteIndex = next.aiManagers.filter((manager) => manager.strategy === 'elite').length;
  const globalIndex = next.aiManagers.length;
  const id = nextId(next, 'ai');
  const headquartersId = AI_HEADQUARTERS[(state.seed + globalIndex + 7) % AI_HEADQUARTERS.length];
  const headquarters = airportById(headquartersId);
  const manager: AiManager = {
    id,
    name: generatedName(ELITE_AI_NAMES, eliteIndex),
    strategy: 'elite',
    cash: ELITE_AI_STARTING_CASH,
    reputation: 75,
    headquartersId,
    hubs: [{
      airportId: headquartersId,
      level: 1,
      weeklyCost: hubWeeklyCost(headquarters),
      builtWeek: next.week,
      specialization: 'balanced',
    }],
    aircraft: [],
    routes: [],
    loans: [],
    decisions: [],
    routeMemory: {},
  };
  manager.aircraft = [
    createAiAircraft(next, manager, 'ARJ21'),
    createAiAircraft(next, manager, 'ARJ21'),
    createAiAircraft(next, manager, 'ATR72'),
  ];
  next.aiManagers.push(manager);
  next.updatedAt = Date.now();
  return next;
}

export function advanceAiManagersForWeek(state: GameState): GameState {
  let next = clone(state);
  next.aiManagers ??= [];
  for (const manager of next.aiManagers) {
    advanceOneAi(next, manager);
  }
  for (const manager of next.aiManagers.filter((item) => item.bankrupt)) {
    next = delistCompany(next, manager.id);
  }
  if (!next.activeAuction) {
    const target = next.aiManagers.find((manager) => !manager.human && (manager.bankrupt || (manager.noRouteWeeks ?? 0) >= 26));
    if (target) {
      const kind = target.bankrupt ? 'bankruptcy' : 'acquisition';
      next = startAuction(next, target.id, kind);
      const event: GameEvent = {
        id: `event-${next.week}-auction-${target.id}`,
        week: next.week,
        tone: kind === 'bankruptcy' ? 'negative' : 'neutral',
        title: kind === 'bankruptcy' ? `${target.name} 进入破产竞拍` : `${target.name} 发出收购要约竞拍`,
        detail: kind === 'bankruptcy'
          ? `${target.name} 已进入清算程序，所有未破产公司都可以参与收购竞拍。`
          : `${target.name} 已连续 26 周没有运营航线，所有未破产公司都可以参与收购竞价。`,
      };
      next.events = [...(next.events ?? []), event].slice(-36);
    }
  }
  next.updatedAt = Date.now();
  return next;
}
